// ============================================================================
// AUTO-CLASSIFY PREVIEW MODAL  (FSA-2.1)
// ============================================================================
// Shows the auto-classify suggestion list for unclassified entries.
// User can accept/reject per row before applying.

import React, { useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { fsaClassificationService } from 'api/services/fsa/fsaClassificationService';
import type { ID } from 'api/types';
import type { AutoClassifySuggestion } from 'api/types/fsa/classification.types';

interface Props {
  isOpen: boolean;
  trialBalanceId: ID;
  onClose: () => void;
  onApplied: () => void;
}

const AutoClassifyPreviewModal: React.FC<Props> = ({ isOpen, trialBalanceId, onClose, onApplied }) => {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [suggestions, setSuggestions] = useState<AutoClassifySuggestion[]>([]);
  const [rejected, setRejected] = useState<Set<ID>>(new Set());

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setRejected(new Set());
    fsaClassificationService.getSuggestions(trialBalanceId).then(r => {
      setSuggestions(r.data ?? []);
    }).catch((err: any) => {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Failed to load suggestions.');
    }).finally(() => setLoading(false));
  }, [isOpen, trialBalanceId]);

  const acceptedIds = useMemo(
    () => suggestions.filter(s => !rejected.has(s.entryId)).map(s => s.entryId),
    [suggestions, rejected]
  );

  const handleToggle = (entryId: ID) => {
    setRejected(prev => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId); else next.add(entryId);
      return next;
    });
  };

  const handleApply = async () => {
    if (acceptedIds.length === 0) {
      notificationService.toast.warning('Nothing to apply — accept at least one suggestion.');
      return;
    }
    setApplying(true);
    try {
      await fsaClassificationService.applyAutoClassify(trialBalanceId, { acceptedEntryIds: acceptedIds });
      notificationService.toast.success(`Applied ${acceptedIds.length} classification(s).`);
      onApplied();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Failed to apply auto-classify.');
    } finally {
      setApplying(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={() => !applying && onClose()} />
      <div className="modal-container modal-lg" style={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Auto-classify Suggestions</h2>
          <button className="modal-close" onClick={onClose} disabled={applying}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading…</div>
          ) : suggestions.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <IconSystem.check size={28} />
              <div style={{ marginTop: 8, fontWeight: 600 }}>No suggestions</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: 4 }}>
                Either all ledgers are already classified, or no hint pattern matched the remaining unmapped names. Classify the rest manually.
              </div>
            </div>
          ) : (
            <>
              <div style={{ padding: '8px 12px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                {acceptedIds.length} of {suggestions.length} accepted. Uncheck rows you want to skip.
              </div>
              <div style={{ border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.825rem' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>✓</th>
                      <th style={thStyle}>Ledger</th>
                      <th style={thStyle}>Group</th>
                      <th style={thStyle}>Proposed mapping</th>
                      <th style={thStyle}>Source</th>
                      <th style={thStyle}>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suggestions.map(s => {
                      const accepted = !rejected.has(s.entryId);
                      const isCustomClass = !s.suggestedClassId && !!s.suggestedCustomClassLocalId;
                      const isScheduleOnly = !s.suggestedClassId && !isCustomClass && !!s.suggestedScheduleId;
                      return (
                        <tr key={String(s.entryId)} style={{ background: accepted ? 'transparent' : '#fef3c7' }}>
                          <td style={tdStyle}>
                            <input
                              type="checkbox"
                              checked={accepted}
                              onChange={() => handleToggle(s.entryId)}
                            />
                          </td>
                          <td style={tdStyle}><strong>{s.ledgerName}</strong></td>
                          <td style={tdStyle}>
                            {s.parentGroup
                              ? <span style={groupChipStyle}>{s.parentGroup}</span>
                              : <span style={{ color: 'var(--text-muted, #94a3b8)', fontStyle: 'italic', fontSize: '0.75rem' }}>(no group)</span>}
                          </td>
                          <td style={tdStyle}>
                            {isScheduleOnly ? (
                              <>
                                <span style={scheduleOnlyChipStyle} title="Schedule-only — leaf class deferred">
                                  📋 {s.suggestedScheduleCode}
                                </span>
                                <span style={{ marginLeft: 6, color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                                  {s.suggestedScheduleName} · defer class
                                </span>
                              </>
                            ) : isCustomClass ? (
                              <>
                                <span style={{ ...scheduleOnlyChipStyle, background: '#ede9fe', color: '#5b21b6' }}
                                      title="Engagement-time custom class carried from prior year">
                                  ✨ {s.suggestedCustomClassName}
                                </span>
                                {s.suggestedScheduleCode && (
                                  <span style={{ marginLeft: 6, color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                                    in {s.suggestedScheduleCode}
                                  </span>
                                )}
                              </>
                            ) : (
                              <>
                                <code style={{ fontSize: '0.78rem' }}>{s.suggestedClassCode}</code>
                                {' · '}
                                <span>{s.suggestedClassName}</span>
                                {s.suggestedScheduleCode && (
                                  <span style={{ marginLeft: 6, color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                                    ({s.suggestedScheduleCode})
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                          <td style={tdStyle}>
                            <span style={sourcePillStyle(s.source)} title={sourceTitle(s.source)}>
                              {sourceLabel(s.source)}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{s.reason ?? ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={applying}>Close</button>
          <button
            className="btn btn-primary"
            onClick={handleApply}
            disabled={loading || applying || acceptedIds.length === 0}
          >
            {applying ? 'Applying…' : `Apply ${acceptedIds.length} classification${acceptedIds.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </>
  );
};

const thStyle: React.CSSProperties = {
  background: '#dbeafe',
  padding: '6px 10px',
  textAlign: 'left',
  fontWeight: 600,
  borderBottom: '2px solid #93c5fd',
  fontSize: '0.75rem',
};
const tdStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderBottom: '1px solid var(--border-color)',
};
const scheduleOnlyChipStyle: React.CSSProperties = {
  padding: '2px 8px',
  borderRadius: 4,
  background: '#fef3c7',
  color: '#92400e',
  fontSize: '0.72rem',
  fontWeight: 600,
};
// Group chip — neutral grey, distinct from class/schedule chips.
const groupChipStyle: React.CSSProperties = {
  padding: '2px 8px',
  borderRadius: 4,
  background: '#f1f5f9',
  color: '#334155',
  fontSize: '0.72rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
};

// FSA-2.1.5 — source labels for the new four-stage resolver. The 'HINT'
// catch-all from FSA-2.1 is no longer emitted by the backend.
function sourceLabel(src: string): string {
  switch (src) {
    case 'PRIOR_ENGAGEMENT':         return 'PRIOR YEAR';
    case 'PRIOR_ENGAGEMENT_CUSTOM':  return 'PRIOR YEAR (CUSTOM)';
    case 'HINT_GROUP_CLASS':         return 'GROUP → CLASS';
    case 'HINT_GROUP_SCHEDULE':      return 'GROUP → NOTE';
    case 'HINT_LEDGER_SCOPED':       return 'SCOPED NAME';
    case 'HINT_LEDGER_GLOBAL':       return 'NAME MATCH';
    default:                         return src;
  }
}
function sourceTitle(src: string): string {
  switch (src) {
    case 'PRIOR_ENGAGEMENT':         return 'Leaf classification carried over from the prior engagement\'s TB.';
    case 'PRIOR_ENGAGEMENT_CUSTOM':  return 'Engagement-time custom-class mapping carried over from the prior engagement (custom class declared in the Note Modal, preserved via Roll Forward).';
    case 'HINT_GROUP_CLASS':         return 'Group pattern matched a hint that targets a specific leaf class.';
    case 'HINT_GROUP_SCHEDULE':      return 'Group pattern matched a schedule-targeting hint; leaf deferred (schedule-only).';
    case 'HINT_LEDGER_SCOPED':       return 'Ledger-name pattern matched within the schedule resolved by stage 1.';
    case 'HINT_LEDGER_GLOBAL':       return 'Ledger-name pattern matched globally (fallback — no group resolution).';
    default:                         return src;
  }
}
function sourcePillStyle(src: string): React.CSSProperties {
  // Blue=prior-leaf, purple=prior-custom, dark amber=group/scope, light=name match.
  let bg = '#fef3c7', fg = '#92400e';
  if (src === 'PRIOR_ENGAGEMENT')             { bg = '#dbeafe'; fg = '#1e40af'; }
  else if (src === 'PRIOR_ENGAGEMENT_CUSTOM') { bg = '#ede9fe'; fg = '#5b21b6'; }
  else if (src === 'HINT_LEDGER_GLOBAL')      { bg = '#f1f5f9'; fg = '#475569'; }
  return {
    padding: '2px 6px',
    borderRadius: 4,
    background: bg,
    color: fg,
    fontSize: '0.7rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
}

export default AutoClassifyPreviewModal;
